import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  Panel,
  PublicLandingHeader,
  describeAuthFormError,
  formErrorClassName,
  formFieldClassName,
  formInputClassName,
  getInitials,
  linkClassName,
  primaryLinkButtonClassName,
  secondaryLinkButtonClassName,
  secondaryTextClassName,
  useAuth,
  type AuthFormError,
  userInitials,
  greetingFirstName,
} from '@monecolier/ui';
import {
  createParentProfile,
  getAggregatedParentFinancialOverview,
  getMe,
  listEnrollments,
  listOwnEnrollmentDocuments,
  listReenrollableStudents,
  type AggregatedParentFinancialOverview,
  type EnrollmentStatusEntry,
  type ParentStudentFinancialOverview,
  type ReenrollableCandidate,
} from '@monecolier/api-client';
import { AggregatedFinancialPanel } from '../components/AggregatedFinancialPanel';
import { ChildStudentAccessPanel } from '../components/ChildStudentAccessPanel';
import { EnrollmentStatusBadge } from '../components/EnrollmentStatusBadge';
import { isCollegeLevel } from '../lib/studentAccess';
import { DepositPaymentProofModal } from '../components/payments/DepositPaymentProofModal';
import {
  CARD_TONE_BY_STATUS,
  CARD_ACCENT_CLASSNAMES,
  deriveCardStatus,
  levelLabel,
  type CardStatus,
  type DocumentsFetchState,
} from '../lib/enrollmentCardStatus';

// AUT-17 — `initialsFromFullName()`/`firstNameFromFullName()` (UX-11B/UX-34),
// qui vivaient ici faute de champ dédié sur `User`, sont remplacées par
// `userInitials()`/`greetingFirstName()` (`@monecolier/ui`, partagées avec
// web-admin). Elles utilisent `firstName`/`lastName` quand ils sont renseignés
// et retombent sur l'ancienne heuristique `fullName` sinon — un compte antérieur
// à AUT-17 s'affiche donc EXACTEMENT comme avant.

// Maquette "Parent Dashboard.dc.html" — chaque carte d'enfant résume l'état de
// SA demande d'inscription (une seule catégorie de statut affichée à la fois,
// même choix éditorial que la maquette). `deriveCardStatus()` combine trois
// sources DÉJÀ RÉELLES de ce projet (jamais une donnée fabriquée) :
//  - `EnrollmentStatusEntry.status`/`waitlistPosition` (ENR-01/04/06/13, GET
//    /enrollments) ;
//  - la validation des documents requis (ENR-07, GET /enrollments/:id/documents)
//    — mêmes règles que le gate serveur `hasAllRequiredDocumentsValidated()`
//    (aucun document requis configuré = jamais bloquant) ;
//  - le solde restant (PAY-01/PAR-05, déjà chargé pour le panneau agrégé
//    ci-dessous, pas de second appel réseau pour cette seule carte).
//
// UX-38 — `CardStatus`/`deriveCardStatus()`/`CARD_ACCENT_CLASSNAMES`/
// `CARD_TONE_BY_STATUS`/`levelLabel()` EXTRAITS vers
// `../lib/enrollmentCardStatus.ts` (comportement STRICTEMENT INCHANGÉ, refactor
// pur) — réutilisés TELS QUELS par la nouvelle page `Inscriptions.tsx`
// (`ChildCard`), jamais dupliqués. Le switch `<StatusTag>` est de même EXTRAIT
// vers `../components/EnrollmentStatusBadge.tsx`.

interface EnrollmentCardProps {
  entry: EnrollmentStatusEntry;
  status: CardStatus;
  index: number;
  // PAY-24 : situation financière déjà chargée pour CET enfant précis (panneau
  // agrégé, PAR-05) — `null` tant qu'elle n'a pas encore de situation
  // financière (déclaré mais jamais scolarisé), jamais un second appel réseau.
  financialOverview: ParentStudentFinancialOverview | null;
  // PAY-16 : ouvre la modale de dépôt de preuve de paiement pour CET enfant —
  // remplace l'ancien bouton `disabled` placeholder (UX-11B).
  onDepositProof: (studentId: string) => void;
  // STU-02 : section "Espace élève" — une seule carte par enfant la porte
  // (inscription APPROVED au collège), voir `studentAccessCardIds` plus bas.
  showStudentAccess: boolean;
}

function EnrollmentCard({ entry, status, index, financialOverview, onDepositProof, showStudentAccess }: EnrollmentCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Panel
      data-testid={`enrollment-status-${index}`}
      className={`flex flex-col gap-2 ${CARD_ACCENT_CLASSNAMES[status.kind]}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <Avatar initials={getInitials(entry.childFirstName, entry.childLastName)} tone={CARD_TONE_BY_STATUS[status.kind]} />
          <span className="text-sm font-semibold text-gray-900">
            {entry.childFirstName} {entry.childLastName}
          </span>
        </span>
        <span data-testid={`enrollment-status-tag-${index}`}>
          <EnrollmentStatusBadge status={status} />
        </span>
      </div>

      <p className={secondaryTextClassName}>
        {levelLabel(entry.requestedLevel)} · {entry.schoolName}
      </p>

      {/* PAY-24 : badge d'avoir PROPRE à cet enfant — purement informatif,
          visible UNIQUEMENT si le montant pour CET enfant précis est > 0. */}
      {financialOverview && Number(financialOverview.adjustmentsTotal) > 0 ? (
        <span
          data-testid={`enrollment-adjustments-badge-${index}`}
          className="w-fit rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
        >
          {t('enrollmentCard.adjustmentBadge', {
            amount: financialOverview.adjustmentsTotal,
            currency: financialOverview.student.currency,
          })}
        </span>
      ) : null}

      {status.kind === 'rejected' && status.reason ? (
        <p className={secondaryTextClassName}>{t('enrollmentCard.reason', { reason: status.reason })}</p>
      ) : null}

      {status.kind === 'documentsMissing' ? (
        <div className="flex flex-wrap gap-1" data-testid={`enrollment-missing-documents-${index}`}>
          {status.missing.map((name) => (
            <span key={name} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
              {name} ✗
            </span>
          ))}
        </div>
      ) : null}

      {status.kind === 'enrolled' ? (
        <div className="flex flex-wrap gap-1">
          <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs text-brand-700">
            {t('enrollmentCard.tuitionPaid')}
          </span>
          <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs text-brand-700">
            {t('enrollmentCard.fileComplete')}
          </span>
        </div>
      ) : null}

      {/* PAY-01 : lien systématique vers la situation financière — un Student
          existe DÈS la soumission (même transaction que l'Enrollment, ENR-01),
          la page cible affiche simplement 0 XOF si rien n'a encore été
          facturé, jamais une erreur. */}
      <div className="flex flex-wrap gap-2 mt-1">
        {status.kind === 'documentsMissing' ? (
          <Button
            size="small"
            data-testid={`enrollment-primary-action-${index}`}
            onClick={() => navigate(`/enrollments/${entry.id}/documents`)}
          >
            {t('enrollmentCard.depositDocument')}
          </Button>
        ) : null}
        {status.kind === 'waitlisted' ? (
          <Button
            variant="secondary"
            size="small"
            data-testid={`enrollment-primary-action-${index}`}
            onClick={() => navigate(`/enrollments/${entry.id}/documents`)}
          >
            {t('enrollmentCard.followRequest')}
          </Button>
        ) : null}
        {status.kind === 'pending' ? (
          <Button
            variant="secondary"
            size="small"
            data-testid={`enrollment-primary-action-${index}`}
            onClick={() => navigate(`/enrollments/${entry.id}/documents`)}
          >
            {t('enrollmentCard.viewDocuments')}
          </Button>
        ) : null}
        {status.kind === 'reenrollmentPendingPayment' ? (
          <Button
            size="small"
            data-testid={`enrollment-primary-action-${index}`}
            onClick={() => navigate(`/students/${entry.studentId}/financial-overview`)}
          >
            {t('enrollmentCard.viewInvoice')}
          </Button>
        ) : null}
        {/* ENR-22 — RÉUTILISE le bouton déjà existant pour `paymentPending`
            (même testid/libellé/navigation), jamais un second bouton créé —
            "Voir les documents"/"Documents à fournir" reste affiché EN PLUS
            si des documents sont AUSSI requis, via le lien inconditionnel
            plus bas, indépendant de `status.kind`. */}
        {status.kind === 'paymentPending' || status.kind === 'pendingPaymentRequired' ? (
          <Button
            size="small"
            data-testid={`enrollment-primary-action-${index}`}
            onClick={() => navigate(`/students/${entry.studentId}/financial-overview`)}
          >
            {t('enrollmentCard.viewBalance')}
          </Button>
        ) : null}
        {/* PAY-16 — l'emplacement réservé par UX-11B (bouton `disabled`) est
            désormais un VRAI bouton, ouvrant la modale de dépôt de preuve de
            paiement (`DepositPaymentProofModal`, réutilise le mécanisme
            d'upload ENR-07 tel quel). Pure traçabilité — n'affecte JAMAIS la
            facture ni le solde affichés sur cette carte.
            PAY-30 — étendu à `pendingPaymentRequired` (frais d'inscription
            dû, demande encore PENDING) : un parent peut légitimement vouloir
            prouver un paiement hors système AVANT même que l'école ne
            décide, exactement comme pour `paymentPending` (scolarité en
            retard, dossier déjà APPROVED) — même bouton, MÊME condition que
            "Voir le solde" juste au-dessus. `DepositPaymentProofModal`
            réutilisée TELLE QUELLE : ni prop ni garde serveur liée au statut
            d'inscription (`PaymentProof` n'a structurellement aucun
            `invoiceId`/`enrollmentId`, seul `requireParentOwnsStudent()`
            protège la route). */}
        {status.kind === 'paymentPending' || status.kind === 'pendingPaymentRequired' ? (
          <Button
            variant="secondary"
            size="small"
            onClick={() => onDepositProof(entry.studentId)}
            data-testid={`enrollment-payment-proof-${index}`}
          >
            {t('enrollmentCard.depositProof')}
          </Button>
        ) : null}
        {status.kind === 'enrolledUnknownBalance' ? (
          <Button
            variant="secondary"
            size="small"
            data-testid={`enrollment-primary-action-${index}`}
            onClick={() => navigate(`/students/${entry.studentId}/financial-overview`)}
          >
            {t('enrollmentCard.viewFinancialSituation')}
          </Button>
        ) : null}
        {status.kind === 'enrolled' ? (
          <Button
            variant="secondary"
            size="small"
            data-testid={`enrollment-primary-action-${index}`}
            onClick={() => navigate(`/students/${entry.studentId}/financial-overview`)}
          >
            {t('enrollmentCard.viewReceipt')}
          </Button>
        ) : null}
        <Link
          to={`/students/${entry.studentId}/financial-overview`}
          className={`${linkClassName} text-xs`}
          data-testid={`financial-overview-link-${index}`}
        >
          {t('enrollmentCard.financialSituationLink')}
        </Link>
        {entry.status === 'PENDING' || entry.status === 'WAITLISTED' ? (
          <Link
            to={`/enrollments/${entry.id}/documents`}
            className={`${linkClassName} text-xs`}
            data-testid={`enrollment-documents-link-${index}`}
          >
            {t('enrollmentCard.documentsLink')}
          </Link>
        ) : null}
      </div>

      {showStudentAccess ? <ChildStudentAccessPanel studentId={entry.studentId} /> : null}
    </Panel>
  );
}

/**
 * UX-59 — accueil PUBLIC (visiteur NON connecté), remplace l'ancien message
 * "Non connecté. Se connecter ou créer un compte." Référence Claude Design du
 * 01/09. `PublicLandingHeader` (packages/ui) PARTAGÉ avec l'équivalent
 * web-admin (Étape 0) — le HERO (icône cœur + titre + 3 cartes éditoriales)
 * reste PAGE-LOCAL.
 *
 * ⚠️ Carte 3 ("Rester informé") — texte volontairement DIFFÉRENT de la
 * maquette d'origine, qui mentionnait des "rappels sur WhatsApp" : contredit
 * la décision déjà verrouillée du projet (WSP-02/UX-19) — WhatsApp livre
 * UNIQUEMENT le reçu PDF de paiement, toute relance reste STRICTEMENT
 * email-only. Ne jamais réintroduire "rappel" + "WhatsApp" dans la même
 * phrase.
 */
function PublicParentLanding() {
  const { t } = useTranslation();
  return (
    <div
      className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
      data-testid="auth-status-anonymous"
    >
      <PublicLandingHeader portalLabel={t('home.public.portalLabel')} />
      <div className="flex flex-col items-center gap-4 p-4 text-center sm:p-8">
        <span aria-hidden="true" className="text-4xl">
          ❤️
        </span>
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{t('home.public.title')}</h1>
        <p className={`max-w-xl ${secondaryTextClassName}`}>{t('home.public.subtitle')}</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/login" className={primaryLinkButtonClassName}>
            {t('home.public.login')}
          </Link>
          <Link to="/register" className={secondaryLinkButtonClassName}>
            {t('home.public.register')}
          </Link>
        </div>

        <div className="mt-4 flex w-full flex-col items-center gap-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('home.public.featuresTitle')}</p>
          <div className="grid w-full max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3">
            <Panel className="text-left">
              <span aria-hidden="true" className="text-2xl">
                📝
              </span>
              <p className="mt-2 text-sm font-semibold text-gray-900">{t('home.public.feature1Title')}</p>
              <p className={secondaryTextClassName}>{t('home.public.feature1Body')}</p>
            </Panel>
            <Panel className="text-left">
              <span aria-hidden="true" className="text-2xl">
                💳
              </span>
              <p className="mt-2 text-sm font-semibold text-gray-900">{t('home.public.feature2Title')}</p>
              <p className={secondaryTextClassName}>{t('home.public.feature2Body')}</p>
            </Panel>
            <Panel className="text-left">
              <span aria-hidden="true" className="text-2xl">
                🔔
              </span>
              <p className="mt-2 text-sm font-semibold text-gray-900">{t('home.public.feature3Title')}</p>
              <p className={secondaryTextClassName}>{t('home.public.feature3Body')}</p>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Home() {
  const { isAuthenticated, user, accessToken, apiClient } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [enrollments, setEnrollments] = useState<EnrollmentStatusEntry[] | null>(null);
  // ENR-08 : candidats à la réinscription — même écran d'accueil que les demandes
  // ENR-01, source distincte (élèves déjà connus, pas une nouvelle demande).
  const [reenrollable, setReenrollable] = useState<ReenrollableCandidate[] | null>(null);

  // Vérification post-hoc PAR-03 (07/08) : un compte authentifié SANS profil parent
  // (`GET /me#hasParentProfile === false`) pouvait se produire (ex. un utilisateur qui
  // s'était inscrit via le portail école, sans téléphone, se connecte quand même ici)
  // sans AUCUN chemin pour compléter son profil — `POST /parents/children`
  // (déclaration d'enfant, PAR-02) le bloquait avec `404 parent_profile_required`,
  // affiché tel quel comme une erreur générique, sans jamais proposer de le corriger.
  // `POST /parents/profile` (PAR-01) fonctionnait déjà parfaitement côté backend —
  // seul le chemin frontend manquait. `null` = pas encore résolu (évite d'afficher le
  // bandeau en un flash avant que `GET /me` n'ait répondu).
  const [hasParentProfile, setHasParentProfile] = useState<boolean | null>(null);
  const [completeProfilePhone, setCompleteProfilePhone] = useState('');
  const [completeProfileSubmitting, setCompleteProfileSubmitting] = useState(false);
  const [completeProfileError, setCompleteProfileError] = useState<AuthFormError | null>(null);

  // PAR-05 : vue d'ensemble agrégée (fratrie, tous tenants confondus) — `null` tant
  // que non résolue (évite d'afficher un flash de contenu vide avant la réponse),
  // `.catch` silencieux (même précédent que `enrollments`/`reenrollable` ci-dessus) :
  // une panne réseau sur ce panneau secondaire ne doit jamais bloquer le reste de la
  // page d'accueil. Réutilisée aussi par les cartes ci-dessous pour dériver
  // "Scolarité payée" — `aggregatedOverviewSettled` distingue "pas encore résolue"
  // de "résolue mais vide/en échec", pour ne jamais rester bloqué sur "Chargement..."
  // indéfiniment (maquette "Parent Dashboard.dc.html").
  const [aggregatedOverview, setAggregatedOverview] = useState<AggregatedParentFinancialOverview | null>(null);
  const [aggregatedOverviewSettled, setAggregatedOverviewSettled] = useState(false);

  // Maquette "Parent Dashboard.dc.html" — état des documents requis PAR
  // inscription (ENR-07), nécessaire pour distinguer "Document manquant" de
  // "Dossier complet" sur chaque carte. `'error'` distinct de `undefined` :
  // jamais interprété comme "aucun document requis" (qui rendrait la carte
  // faussement complète), voir `missingRequiredDocuments()` ci-dessus.
  const [documentsByEnrollment, setDocumentsByEnrollment] = useState<Record<string, DocumentsFetchState>>({});

  // PAY-16 : élève ciblé par la modale de dépôt de preuve de paiement, ou
  // `null` si fermée — même précédent que `viewingReceiptRow`/PAY-20 (une
  // seule modale de ce type ouverte à la fois).
  const [depositProofStudentId, setDepositProofStudentId] = useState<string | null>(null);
  const [depositProofSuccessMessage, setDepositProofSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;
    listEnrollments(apiClient, accessToken)
      .then((response) => setEnrollments(response.enrollments))
      .catch(() => setEnrollments([]));
    listReenrollableStudents(apiClient, accessToken)
      .then((response) => setReenrollable(response.candidates))
      .catch(() => setReenrollable([]));
    getMe(apiClient, accessToken)
      .then((response) => setHasParentProfile(response.hasParentProfile))
      .catch(() => setHasParentProfile(null));
    getAggregatedParentFinancialOverview(apiClient, accessToken)
      .then((response) => setAggregatedOverview(response))
      .catch(() => setAggregatedOverview(null))
      .finally(() => setAggregatedOverviewSettled(true));
  }, [isAuthenticated, accessToken, apiClient]);

  // Maquette "Parent Dashboard.dc.html" — l'état des documents ne change ce qui
  // est affiché que pour PENDING/APPROVED (voir `deriveCardStatus()`) : WAITLISTED
  // affiche sa position en priorité, REJECTED/CANCELLED/REENROLLMENT_PENDING_PAYMENT
  // n'en dépendent pas — jamais un appel réseau pour rien.
  useEffect(() => {
    if (!accessToken || !enrollments) return;
    const relevant = enrollments.filter((entry) => entry.status === 'PENDING' || entry.status === 'APPROVED');
    if (relevant.length === 0) return;
    Promise.all(
      relevant.map((entry) =>
        listOwnEnrollmentDocuments(apiClient, entry.id, accessToken)
          .then((response) => [entry.id, response.items] as const)
          .catch(() => [entry.id, 'error' as const] as const),
      ),
    ).then((pairs) => {
      setDocumentsByEnrollment((current) => {
        const next = { ...current };
        for (const [id, state] of pairs) next[id] = state;
        return next;
      });
    });
  }, [enrollments, accessToken, apiClient]);

  const pendingReenrollments = (reenrollable ?? []).filter((c) => !c.alreadyReenrolled);

  function financialOverviewForStudent(studentId: string): ParentStudentFinancialOverview | null {
    return aggregatedOverview?.children.find((child) => child.student.id === studentId) ?? null;
  }

  async function handleCompleteProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!accessToken) return;
    setCompleteProfileError(null);
    setCompleteProfileSubmitting(true);
    try {
      await createParentProfile(apiClient, { phone: completeProfilePhone }, accessToken);
      setHasParentProfile(true);
      setCompleteProfilePhone('');
    } catch (err) {
      setCompleteProfileError(describeAuthFormError(err));
    } finally {
      setCompleteProfileSubmitting(false);
    }
  }

  // UX-59 : accueil PUBLIC dédié pour un visiteur non connecté — voir
  // `PublicParentLanding` ci-dessus. Les hooks ci-dessus restent TOUS appelés
  // inconditionnellement avant ce retour anticipé (règle des Hooks React).
  // Corrige au passage le bug confirmé : "Gérer mes inscriptions"/"Paiements"
  // (tout en bas de ce composant) ne sont plus jamais atteints pour un
  // visiteur anonyme — plus de rendu conditionnel `disabled`/CSS à contourner,
  // un vrai retour anticipé React qui ne les monte jamais dans le DOM.
  if (!isAuthenticated || !user) {
    return <PublicParentLanding />;
  }

  // STU-02 — une seule carte par enfant porte la section "Espace élève" : la
  // première inscription APPROVED au collège (seul cycle éligible, même règle
  // que le serveur). Le panneau lui-même ne s'affiche que si l'école a créé
  // le compte.
  const studentAccessCardIds = new Set<string>();
  const studentsWithAccessCard = new Set<string>();
  for (const entry of enrollments ?? []) {
    if (
      entry.status === 'APPROVED' &&
      isCollegeLevel(entry.requestedLevel) &&
      !studentsWithAccessCard.has(entry.studentId)
    ) {
      studentsWithAccessCard.add(entry.studentId);
      studentAccessCardIds.add(entry.id);
    }
  }

  return (
    <Card title={t('home.cardTitle')} size="page">
      {/* UX-34 — UN SEUL en-tête ("Bonjour, [prénom]"), même précédent EXACT
          que `CashierHome`/`apps/web-admin/src/pages/Home.tsx` : plus de texte
          de débogage ("Connecté en tant que X (email)", déjà présent dans le
          pied de Sidebar), plus de bouton "Se déconnecter" en double (idem),
          plus de ligne "Aujourd'hui" (absente de la référence Claude Design,
          aucune fonction claire identifiée). Rendu INCONDITIONNELLEMENT pour
          TOUT compte authentifié — vérifié en Étape 0 qu'aucune condition de
          rendu par type de compte (co-parent ou non) n'existe dans ce fichier,
          voir docs/architecture.md, section "UX-34", pour le détail complet. */}
      <div data-testid="auth-status-authenticated" className="flex items-center gap-2">
        <Avatar initials={userInitials(user)} />
        <h2 className="text-xl font-semibold text-gray-900">{t('home.greeting', { name: greetingFirstName(user) })}</h2>
      </div>
      <p className={secondaryTextClassName}>{t('home.statusSubtitle')}</p>

      {/* PAR-05 : sélecteur d'enfant PROÉMINENT + vue d'ensemble agrégée, tous
          tenants (écoles) confondus — en haut de l'espace parent, comme demandé.
          Refonte de navigation (Hybride) : extrait dans `AggregatedFinancialPanel`
          (`../components`), réutilisé TEL QUEL par la nouvelle page `/payments`
          (onglet "Paiements") — même comportement qu'avant l'extraction. */}
      <AggregatedFinancialPanel overview={aggregatedOverview} />

      {/* Complétion du profil parent — voir le commentaire sur `hasParentProfile`
          ci-dessus. Réutilise POST /parents/profile (PAR-01) tel quel, jamais un
          second mécanisme de création. */}
      {hasParentProfile === false ? (
        <div
          data-testid="complete-parent-profile-banner"
          className="rounded-md border border-gray-200 p-3 flex flex-col gap-2"
        >
          <p className={secondaryTextClassName}>{t('home.completeProfile.message')}</p>
          <form onSubmit={handleCompleteProfile} className="flex items-end gap-2 flex-wrap">
            <label className={formFieldClassName}>
              {t('home.completeProfile.phoneLabel')}
              <input
                type="tel"
                value={completeProfilePhone}
                onChange={(event) => setCompleteProfilePhone(event.target.value)}
                required
                autoComplete="tel"
                placeholder="+221 77 123 45 67"
                data-testid="complete-parent-profile-phone"
                className={formInputClassName}
              />
            </label>
            <Button type="submit" disabled={completeProfileSubmitting} data-testid="complete-parent-profile-submit">
              {completeProfileSubmitting ? t('home.completeProfile.submitting') : t('home.completeProfile.submit')}
            </Button>
          </form>
          {completeProfileError ? (
            <p
              role="alert"
              data-testid="complete-parent-profile-error"
              className={formErrorClassName(completeProfileError.kind)}
            >
              {completeProfileError.message}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Maquette "Parent Dashboard.dc.html" — une carte par demande d'inscription,
          statut résumé en un coup d'œil. ENR-01, point 3. */}
      {enrollments && enrollments.length > 0 ? (
        <div data-testid="enrollment-status-list" className="flex flex-col gap-3">
          {enrollments.map((entry, index) => {
            const financialOverview = financialOverviewForStudent(entry.studentId);
            const showStudentAccess = studentAccessCardIds.has(entry.id);
            const status = deriveCardStatus(entry, documentsByEnrollment[entry.id], financialOverview, aggregatedOverviewSettled);
            return (
              <EnrollmentCard
                key={entry.id}
                entry={entry}
                status={status}
                index={index}
                financialOverview={financialOverview}
                showStudentAccess={showStudentAccess}
                onDepositProof={(studentId) => {
                  setDepositProofSuccessMessage(null);
                  setDepositProofStudentId(studentId);
                }}
              />
            );
          })}
        </div>
      ) : null}

      {/* UX-34 — état "zéro enfant" : `enrollments` résolu (pas `null`, la
          première requête a répondu) et VIDE. Remplace le vide qui suivait
          auparavant le bloc d'en-tête, exactement le cas réel du compte
          `khalebiosdev@gmail.com` signalé par ce ticket ("aucun enfant
          déclaré... ce bloc s'affiche seul, sans contenu riche en dessous").
          UX-38 — CTA "+ Inscrire un enfant" -> `/inscriptions` (au lieu de
          `/enroll`) : nouvelle page unifiée, qui redirige elle-même vers
          `/children/new` si aucun enfant n'est encore déclaré, exactement le
          même comportement en cascade qu'`Enroll.tsx` avant ce ticket. */}
      {enrollments && enrollments.length === 0 ? (
        <EmptyState
          data-testid="home-empty-state"
          icon="👪"
          title={t('home.empty.title')}
          body={t('home.empty.body')}
          actionLabel={t('home.empty.action')}
          onAction={() => navigate('/inscriptions')}
        />
      ) : null}

      {/* PAR-10 : bannière informative pure (WSP-02, déjà fonctionnel côté
          backend depuis longtemps, jamais mentionné côté UI) — aucun appel
          réseau, aucune donnée calculée. Masquée quand la liste est vide :
          rien à confirmer tant qu'aucune inscription n'a de raison de
          générer une confirmation/un reçu (voir docs/architecture.md,
          section "PAR-10", pour le détail du choix). Même style que les
          badges "Scolarité payée ✓"/"Inscrite ✓" déjà en place (fond vert
          clair `bg-brand-*`, ton neutre). */}
      {enrollments && enrollments.length > 0 ? (
        <p
          data-testid="whatsapp-notifications-banner"
          className="flex items-center gap-1.5 rounded-md bg-brand-50 px-3 py-2 text-xs text-brand-700"
        >
          <span aria-hidden="true">✓</span>
          {t('home.whatsappBanner')}
        </p>
      ) : null}

      {/* ENR-08, point 3 : élèves déjà connus qui restent À réinscrire pour la
          prochaine année scolaire (les déjà-réinscrits/en attente de paiement
          apparaissent déjà ci-dessus via leur propre Enrollment). */}
      {/* UX-38 — mène désormais vers `/inscriptions` (au lieu de `/reenroll`
          directement) : la section "Réinscriptions disponibles" y vit
          désormais, réutilisant EXACTEMENT la même logique d'éligibilité —
          voir `ReenrollmentSection`. */}
      {pendingReenrollments.length > 0 ? (
        <Panel data-testid="reenrollment-reminder" className="flex flex-wrap items-center justify-between gap-2 border-l-4 border-l-amber-600">
          <p className={secondaryTextClassName}>
            {t('home.reenrollmentReminder', { count: pendingReenrollments.length })}
          </p>
          <Button variant="secondary" size="small" onClick={() => navigate('/inscriptions')}>
            {t('home.reenroll')}
          </Button>
        </Panel>
      ) : null}

      {/* UX-38 — Option B (décidée avec le PO) : `Home.tsx` reste un aperçu
          RAPIDE (statut par carte, panneau agrégé, rappels) — la gestion
          complète (déclarer/modifier/supprimer un enfant, nouvelle demande,
          réinscription) vit désormais SUR UNE SEULE page dédiée,
          `/inscriptions`, qui reprend et enrichit tout ce que faisaient
          `Children.tsx`/`Enroll.tsx`/`Reenroll.tsx` (désormais retirés). Les 3
          anciens boutons ("Voir mes enfants"/"Faire une demande
          d'inscription"/"Réinscription") sont donc FUSIONNÉS en un seul —
          jamais trois boutons menant tous à la même destination, ni un
          bouton menant vers une ancienne route qui ne fait plus que
          rediriger. "Paiements" reste INCHANGÉ, hors périmètre de ce ticket. */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => navigate('/inscriptions')}>{t('home.manageEnrollments')}</Button>
        <Button variant="secondary" onClick={() => navigate('/payments')}>
          {t('home.payments')}
        </Button>
      </div>

      {depositProofSuccessMessage ? (
        <p data-testid="deposit-payment-proof-success" className={secondaryTextClassName}>
          {depositProofSuccessMessage}
        </p>
      ) : null}

      {depositProofStudentId ? (
        <DepositPaymentProofModal
          studentId={depositProofStudentId}
          onCancel={() => setDepositProofStudentId(null)}
          onSubmitted={(message) => {
            setDepositProofStudentId(null);
            setDepositProofSuccessMessage(message);
          }}
        />
      ) : null}
    </Card>
  );
}
